import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import api from '../lib/api';
import { 
  Target,
  Eye,
  EyeOff,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  BarChart3,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Users,
  ChevronRight,
  Hash,
  Type,
  ListChecks,
  SlidersHorizontal,
  List,
  CircleDot
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend
} from 'recharts';

// Anonymity indicator
function AnonymityIndicator({ responseCount, threshold = 3 }) {
  const meetsThreshold = responseCount >= threshold;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
      meetsThreshold 
        ? 'bg-emerald-50 text-emerald-700' 
        : 'bg-slate-100 text-slate-500'
    }`}>
      {meetsThreshold ? (
        <>
          <Eye className="w-3 h-3" />
          <span>n={responseCount}</span>
        </>
      ) : (
        <>
          <EyeOff className="w-3 h-3" />
          <span>n={responseCount} (masked)</span>
        </>
      )}
    </div>
  );
}

// Driver tab button
function DriverTab({ driver, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`driver-tab-${driver.id}`}
      className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
        isActive 
          ? 'bg-[#5AA2AE] text-white shadow-sm' 
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      {driver.name}
    </button>
  );
}

// Question type icon
function QuestionTypeIcon({ type }) {
  const icons = {
    slider: <SlidersHorizontal className="w-4 h-4" />,
    free_text: <Type className="w-4 h-4" />,
    multiple_choice: <CircleDot className="w-4 h-4" />,
    dropdown: <List className="w-4 h-4" />,
    checkbox: <ListChecks className="w-4 h-4" />,
    multi_topic: <Hash className="w-4 h-4" />,
  };
  return icons[type] || <MessageSquare className="w-4 h-4" />;
}

// Slider visualization with distribution histogram
function SliderVisualization({ result, config }) {
  if (!result) return null;
  
  const minVal = config?.min_value || 0;
  const maxVal = config?.max_value || 10;
  const leftLabel = config?.left_label || 'Low';
  const middleLabel = config?.middle_label || '';
  const rightLabel = config?.right_label || 'High';
  
  // Prepare distribution data
  const bucketLabels = ['0-2', '2-4', '4-6', '6-8', '8-10'];
  const distributionData = (result.distribution || []).map((count, i) => ({
    range: bucketLabels[i] || `${i}`,
    count,
    fill: result.average && (i === Math.floor(result.average / 2)) ? '#5AA2AE' : '#94a3b8'
  }));
  
  // Calculate percentage position for average marker
  const avgPercent = ((result.average - minVal) / (maxVal - minVal)) * 100;
  
  return (
    <div className="space-y-4">
      {/* Average display */}
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-slate-900">{result.average?.toFixed(1)}</span>
          <span className="text-sm text-slate-500">/ {maxVal}</span>
        </div>
        {result.std_dev !== undefined && (
          <Badge variant="outline" className="text-slate-500">
            σ = {result.std_dev?.toFixed(2)}
          </Badge>
        )}
      </div>
      
      {/* Slider bar with average marker */}
      <div className="relative">
        <div className="h-3 bg-gradient-to-r from-red-200 via-amber-200 to-emerald-200 rounded-full" />
        <div 
          className="absolute top-0 w-1 h-3 bg-slate-900 rounded"
          style={{ left: `${avgPercent}%`, transform: 'translateX(-50%)' }}
        />
      </div>
      
      {/* Labels */}
      <div className="flex justify-between text-xs text-slate-500">
        <span>{leftLabel}</span>
        {middleLabel && <span>{middleLabel}</span>}
        <span>{rightLabel}</span>
      </div>
      
      {/* Distribution histogram */}
      {distributionData.length > 0 && (
        <div className="h-24 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={distributionData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey="range" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {distributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// Multiple choice / Dropdown visualization
function ChoiceVisualization({ result, multiSelect }) {
  if (!result?.options?.length) return null;
  
  const colors = ['#5AA2AE', '#7BC4CF', '#9DD5DE', '#BFE6EC', '#E0F4F7'];
  
  return (
    <div className="space-y-3">
      {result.options.slice(0, 6).map((opt, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="truncate flex-1 mr-2 text-slate-700">{opt.label}</span>
            <span className="text-slate-500 shrink-0 font-medium">{opt.percentage}%</span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${opt.percentage}%`, backgroundColor: colors[i % colors.length] }}
            />
          </div>
        </div>
      ))}
      {result.options.length > 6 && (
        <p className="text-xs text-slate-400">+{result.options.length - 6} more options</p>
      )}
      {multiSelect && (
        <p className="text-xs text-slate-400 italic">Multi-select: percentages may exceed 100%</p>
      )}
    </div>
  );
}

// Multi-topic visualization (horizontal bar chart)
function MultiTopicVisualization({ result, questionId, onAnalyze }) {
  if (!result?.topics?.length) return null;
  
  const data = result.topics.slice(0, 8).map(t => ({
    name: t.label?.substring(0, 25) || 'Unknown',
    fullName: t.label || 'Unknown',
    value: t.count,
    percentage: t.percentage
  }));
  
  return (
    <div className="space-y-4">
      {/* Stats row with AI Analysis button */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-slate-600">
            <Hash className="w-4 h-4" />
            <span>{result.total_responses || data.reduce((sum, d) => sum + d.value, 0)} responses</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <span>{result.topics?.length || 0} unique topics</span>
          </div>
        </div>
        {onAnalyze && (
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onAnalyze(questionId)}
            className="text-xs gap-1.5"
          >
            <Lightbulb className="w-3.5 h-3.5" />
            AI Theme Grouping
          </Button>
        )}
      </div>
      
      {/* Horizontal stacked bar list — mobile-friendly, labels always visible */}
      <div className="space-y-2.5">
        {data.map((item, i) => (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-700 font-medium truncate mr-2" title={item.fullName}>
                {item.fullName}
              </span>
              <span className="text-slate-500 whitespace-nowrap">{item.value} ({item.percentage}%)</span>
            </div>
            <div className="h-5 bg-slate-100 rounded overflow-hidden">
              <div 
                className="h-full bg-[#5AA2AE] rounded transition-all duration-500"
                style={{ width: `${(item.value / Math.max(...data.map(d => d.value))) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Free text visualization with AI analysis
function FreeTextVisualization({ result, questionId, projectId, onAnalyze }) {
  if (!result) return null;
  
  const sentimentColors = {
    positive: '#10b981',
    negative: '#ef4444', 
    neutral: '#6b7280'
  };
  
  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-slate-600">
            <MessageSquare className="w-4 h-4" />
            <span>{result.total_responses} responses</span>
          </div>
          {result.avg_length > 0 && (
            <div className="flex items-center gap-1.5 text-slate-600">
              <Type className="w-4 h-4" />
              <span>~{result.avg_length} chars avg</span>
            </div>
          )}
        </div>
        {result.has_ai_analysis && (
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onAnalyze?.(questionId)}
            className="text-xs gap-1.5"
          >
            <Lightbulb className="w-3.5 h-3.5" />
            AI Analysis
          </Button>
        )}
      </div>
      
      {/* Sentiment breakdown */}
      {result.sentiment_breakdown && (
        <div>
          <p className="text-xs text-slate-500 mb-2">Sentiment breakdown:</p>
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
            {result.sentiment_breakdown.positive > 0 && (
              <div 
                className="h-full transition-all" 
                style={{ width: `${result.sentiment_breakdown.positive}%`, backgroundColor: sentimentColors.positive }}
                title={`Positive: ${result.sentiment_breakdown.positive}%`}
              />
            )}
            {result.sentiment_breakdown.neutral > 0 && (
              <div 
                className="h-full transition-all" 
                style={{ width: `${result.sentiment_breakdown.neutral}%`, backgroundColor: sentimentColors.neutral }}
                title={`Neutral: ${result.sentiment_breakdown.neutral}%`}
              />
            )}
            {result.sentiment_breakdown.negative > 0 && (
              <div 
                className="h-full transition-all" 
                style={{ width: `${result.sentiment_breakdown.negative}%`, backgroundColor: sentimentColors.negative }}
                title={`Concerned: ${result.sentiment_breakdown.negative}%`}
              />
            )}
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-emerald-600">{result.sentiment_breakdown.positive}% Positive</span>
            <span className="text-slate-500">{result.sentiment_breakdown.neutral}% Neutral</span>
            <span className="text-red-500">{result.sentiment_breakdown.negative}% Concerned</span>
          </div>
        </div>
      )}
      
      {/* Themes */}
      {result.themes?.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-2">Key themes identified:</p>
          <div className="space-y-2">
            {result.themes.slice(0, 5).map((theme, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm mb-0.5">
                    <span className="text-slate-700">{theme.name}</span>
                    <span className="text-slate-500 text-xs">{theme.percentage}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#5AA2AE] rounded-full transition-all"
                      style={{ width: `${theme.percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Question result card
function QuestionResultCard({ question, threshold = 3, projectId, onAnalyze }) {
  const { id, response_count, result, question_type, heading, text, slider_config, options } = question;
  const meetsThreshold = response_count >= threshold;
  
  return (
    <Card className={`transition-all ${!meetsThreshold ? 'opacity-60' : 'hover:shadow-md'}`}>
      <CardContent className="pt-5 pb-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-slate-100">
              <QuestionTypeIcon type={question_type} />
            </div>
            <Badge variant="outline" className="text-xs capitalize">
              {question_type?.replace('_', ' ')}
            </Badge>
          </div>
          <AnonymityIndicator responseCount={response_count} threshold={threshold} />
        </div>
        
        {/* Question text */}
        <h4 className="font-semibold text-slate-900 mb-1">{heading}</h4>
        {text && text !== heading && (
          <p className="text-sm text-slate-500 mb-4 line-clamp-2">{text}</p>
        )}
        
        {/* Visualization based on question type */}
        {meetsThreshold && result ? (
          <div className="mt-4">
            {question_type === 'slider' && (
              <SliderVisualization result={result} config={slider_config} />
            )}
            {(question_type === 'multiple_choice' || question_type === 'dropdown') && (
              <ChoiceVisualization result={result} multiSelect={false} />
            )}
            {question_type === 'checkbox' && (
              <ChoiceVisualization result={result} multiSelect={true} />
            )}
            {question_type === 'multi_topic' && (
              <MultiTopicVisualization 
                result={result}
                questionId={id}
                onAnalyze={onAnalyze}
              />
            )}
            {question_type === 'free_text' && (
              <FreeTextVisualization 
                result={result} 
                questionId={id}
                projectId={projectId}
                onAnalyze={onAnalyze}
              />
            )}
          </div>
        ) : (
          <div className="h-32 bg-slate-100 rounded-lg flex items-center justify-center mt-4">
            <div className="text-center text-slate-400">
              <EyeOff className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">Need {threshold - response_count} more responses</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DriverAnalysisPage() {
  const { projectId } = useParams();
  const { currentProject } = useProject();
  const [loading, setLoading] = useState(true);
  const [activeDriver, setActiveDriver] = useState(null);
  const [segmentTypeFilter, setSegmentTypeFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState(null);
  
  // AI Analysis state
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Handle AI analysis request
  const handleAiAnalysis = async (questionId) => {
    setAiDialogOpen(true);
    setAiLoading(true);
    setAiAnalysis(null);
    
    try {
      const params = new URLSearchParams();
      params.append('question_id', questionId);
      if (segmentTypeFilter && segmentTypeFilter !== 'all') {
        params.append('segment_type_id', segmentTypeFilter);
      }
      if (segmentFilter && segmentFilter !== 'all') {
        params.append('segment_id', segmentFilter);
      }
      
      const response = await api.post(`/projects/${projectId}/ai-analyze-responses?${params.toString()}`);
      setAiAnalysis(response.data);
    } catch (err) {
      console.error('AI analysis error:', err);
      setAiAnalysis({ error: 'Failed to generate analysis. Please try again.' });
    } finally {
      setAiLoading(false);
    }
  };
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (segmentTypeFilter && segmentTypeFilter !== 'all') {
          params.append('segment_type_id', segmentTypeFilter);
        }
        if (segmentFilter && segmentFilter !== 'all') {
          params.append('segment_id', segmentFilter);
        }
        
        const response = await api.get(`/projects/${projectId}/driver-analysis?${params.toString()}`);
        setAnalysisData(response.data);
        
        // Set first driver as active
        if (response.data.drivers?.length > 0 && !activeDriver) {
          setActiveDriver(response.data.drivers[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch driver analysis:', err);
        setError('Failed to load driver analysis data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [projectId, segmentTypeFilter, segmentFilter]);
  
  // Reset segment filter when segment type changes
  useEffect(() => {
    setSegmentFilter('all');
  }, [segmentTypeFilter]);
  
  // Get available segments for selected segment type
  const availableSegments = useMemo(() => {
    if (!analysisData?.segments || segmentTypeFilter === 'all') return [];
    return analysisData.segments.filter(s => s.segment_type_id === segmentTypeFilter);
  }, [analysisData?.segments, segmentTypeFilter]);
  
  const currentDriverData = activeDriver ? analysisData?.driver_data?.[activeDriver] : null;
  const currentDriver = analysisData?.drivers?.find(d => d.id === activeDriver);
  
  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <Card className="p-8 text-center">
        <p className="text-red-500">{error}</p>
        <Button onClick={() => window.location.reload()} className="mt-4">Retry</Button>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header with Segment Type Filter */}
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Driver Analysis
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            Response analysis by driver • {analysisData?.total_stakeholders || 0} stakeholders
          </p>
        </div>
        
        {/* Segment Type Filter */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Select value={segmentTypeFilter} onValueChange={setSegmentTypeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="segment-type-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Segment Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Segment Types</SelectItem>
              {analysisData?.segment_types?.map(st => (
                <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Segment Filter (shown when segment type selected) */}
          {segmentTypeFilter !== 'all' && availableSegments.length > 0 && (
            <Select value={segmentFilter} onValueChange={setSegmentFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="segment-filter">
                <SelectValue placeholder="Select Segment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Segments</SelectItem>
                {availableSegments.map(seg => (
                  <SelectItem key={seg.id} value={seg.id}>{seg.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      
      {/* Driver Tabs */}
      <div className="relative">
        <ScrollArea className="w-full">
          <div className="flex gap-2 pb-2">
            {analysisData?.drivers?.map(driver => (
              <DriverTab
                key={driver.id}
                driver={driver}
                isActive={activeDriver === driver.id}
                onClick={() => setActiveDriver(driver.id)}
              />
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
      
      {/* Driver Content */}
      {currentDriver && currentDriverData ? (
        <>
          {/* Driver Header */}
          <Card className="bg-gradient-to-r from-slate-50 to-white">
            <CardContent className="py-3 sm:py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-900">{currentDriver.name}</h2>
                  <p className="text-xs sm:text-sm text-slate-500">
                    {currentDriverData.questions?.length || 0} questions • {currentDriverData.total_responses || 0} total responses
                  </p>
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <Users className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">{analysisData.total_stakeholders} stakeholders</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Questions Grid */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#5AA2AE]" />
              Question Results
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {currentDriverData.questions?.map(question => (
                <QuestionResultCard
                  key={question.id}
                  question={question}
                  threshold={3}
                  projectId={projectId}
                  onAnalyze={handleAiAnalysis}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Select a driver to view analysis</p>
          </CardContent>
        </Card>
      )}
      
      {/* Anonymity Notice */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-[#5AA2AE]/10">
              <EyeOff className="w-4 h-4 text-[#5AA2AE]" />
            </div>
            <div>
              <p className="font-medium text-sm">Anonymity Protection Active</p>
              <p className="text-sm text-slate-500">
                Results with fewer than 3 responses are automatically masked. 
                Sample sizes (n=) are displayed for all visible results.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* AI Analysis Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              AI-Powered Response Analysis
            </DialogTitle>
            {aiAnalysis?.question && (
              <DialogDescription>
                Analysis of responses to: "{aiAnalysis.question}"
              </DialogDescription>
            )}
          </DialogHeader>
          
          {aiLoading ? (
            <div className="space-y-4 py-6">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5AA2AE]"></div>
                <span className="text-slate-600">Analyzing {aiAnalysis?.response_count || 'responses'}...</span>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </div>
          ) : aiAnalysis?.error ? (
            <div className="py-6 text-center text-red-500">
              {aiAnalysis.error}
            </div>
          ) : aiAnalysis ? (
            <div className="space-y-6 py-4">
              {/* Key Insight */}
              {aiAnalysis.key_insight && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-amber-900">Key Insight</p>
                      <p className="text-amber-800 text-sm mt-1">{aiAnalysis.key_insight}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Summary */}
              {aiAnalysis.summary && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Summary</h4>
                  <p className="text-slate-600 text-sm">{aiAnalysis.summary}</p>
                </div>
              )}
              
              {/* Themes */}
              {aiAnalysis.themes?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Identified Themes</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {aiAnalysis.themes.map((theme, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            theme.sentiment === 'positive' ? 'bg-emerald-500' :
                            theme.sentiment === 'negative' ? 'bg-red-500' : 'bg-slate-400'
                          }`} />
                          <span className="text-sm text-slate-700">{theme.name}</span>
                        </div>
                        <span className="text-xs text-slate-500">{theme.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Detailed Analysis */}
              {aiAnalysis.detailed_analysis && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Analysis</h4>
                  <div className="space-y-4 text-sm">
                    {aiAnalysis.detailed_analysis.split(/\*\*([A-Z\s]+)\*\*/).filter(Boolean).map((section, i) => {
                      // Check if this is a section header
                      const isHeader = /^[A-Z\s]+$/.test(section.trim());
                      if (isHeader) {
                        const headerColors = {
                          'POSITIVES': 'bg-green-50 text-green-800 border-green-200',
                          'CHALLENGES': 'bg-red-50 text-red-800 border-red-200',
                          'OPPORTUNITIES FOR IMPROVEMENT': 'bg-blue-50 text-blue-800 border-blue-200',
                          'SEGMENT OUTLIERS': 'bg-purple-50 text-purple-800 border-purple-200'
                        };
                        const colorClass = headerColors[section.trim()] || 'bg-slate-50 text-slate-800 border-slate-200';
                        return (
                          <div key={i} className={`px-3 py-1.5 rounded-md border font-semibold text-xs ${colorClass}`}>
                            {section.trim()}
                          </div>
                        );
                      }
                      // Parse bullet points
                      const bullets = section.split(/[•\-\*]/).filter(b => b.trim());
                      if (bullets.length > 0) {
                        return (
                          <ul key={i} className="space-y-2 pl-1">
                            {bullets.map((bullet, j) => (
                              <li key={j} className="flex items-start gap-2 text-slate-600">
                                <span className="text-slate-400 mt-1">•</span>
                                <span>{bullet.trim()}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              )}
              
              {/* Meta info */}
              <div className="border-t pt-4 flex items-center justify-between text-xs text-slate-400">
                <span>Based on {aiAnalysis.response_count} responses</span>
                <span>Generated {new Date(aiAnalysis.analyzed_at).toLocaleString()}</span>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
